// "Highlight with colour" — the designer's plain-words face on a column's
// ADV_FORMAT (visual-engine conditionalFormat.ts is the format and its
// evaluator; this file only translates).
//
//   When <this value> is less than <Target>        → Alert
//   When <this value> is between 0 and 1000        → one colour #f59e0b
//   Everything else                                 → Good
//
// becomes
//
//   #value# < #TARGET#~{"theme":"alert"}$#value# >= 0 && #value# <= 1000~{"backgroundColor":"#f59e0b"}$otherwise~{"theme":"good"}
//
// A rule this screen cannot say in words (hand-written / imported) is kept
// VERBATIM as a "custom" rule, so opening and saving never rewrites it.

import { OTHERWISE_FORMULA, parseAdvFormat } from '@twasta/visual-engine'

export type RuleOp = '<' | '<=' | '>' | '>=' | '=' | '!=' | 'between'
export type RuleKind = 'compare' | 'otherwise' | 'custom'

export interface ColourRule {
  kind: RuleKind
  op: RuleOp
  /** Compare with a number typed in, or with another field of the same row. */
  against: 'number' | 'field'
  value: string
  value2: string
  /** Column NAME when `against` is 'field'. */
  field: string
  /** custom rules only: the formula as written. */
  formula: string
  /** A colour theme id ('alert', 'good', …) — '' when `color` is used. */
  theme: string
  /** One colour for every match ('#rrggbb') — used when `theme` is ''. */
  color: string
  /** Other format keys the rule carried (fontWeight, color, …), kept as-is. */
  extra: Record<string, string>
}

export const OPS: { op: RuleOp; words: string }[] = [
  { op: '<', words: 'is less than' },
  { op: '<=', words: 'is at most' },
  { op: '>', words: 'is more than' },
  { op: '>=', words: 'is at least' },
  { op: '=', words: 'equals' },
  { op: '!=', words: 'is not' },
  { op: 'between', words: 'is between' },
]

export function opWords(op: RuleOp): string {
  return OPS.find((o) => o.op === op)?.words ?? op
}

export function newRule(partial: Partial<ColourRule> = {}): ColourRule {
  return {
    kind: 'compare', op: '<', against: 'number', value: '', value2: '', field: '',
    formula: '', theme: 'alert', color: '', extra: {}, ...partial,
  }
}

const NUM = '(-?\\d+(?:\\.\\d+)?)'
const TOKEN = '#([^#]+)#'
const SIMPLE_RE = new RegExp(`^#value#\\s*(<=|>=|!=|<>|==|=|<|>)\\s*(?:${NUM}|${TOKEN})$`, 'i')
const BETWEEN_RE = new RegExp(`^#value#\\s*>=\\s*${NUM}\\s*&&\\s*#value#\\s*<=\\s*${NUM}$`, 'i')

function splitFormat(format: Record<string, string>): Pick<ColourRule, 'theme' | 'color' | 'extra'> {
  const { theme, backgroundColor, ...rest } = format || {}
  const extra: Record<string, string> = {}
  for (const [k, v] of Object.entries(rest)) extra[k] = String(v)
  return { theme: theme ? String(theme) : '', color: !theme && backgroundColor ? String(backgroundColor) : '', extra:
    theme && backgroundColor ? { ...extra, backgroundColor: String(backgroundColor) } : extra }
}

/** ADV_FORMAT → the rules this screen shows. Never loses a rule. */
export function parseColourRules(adv: string | null | undefined): ColourRule[] {
  const out: ColourRule[] = []
  for (const c of parseAdvFormat(adv)) {
    const formula = c.formula.trim()
    const fmt = splitFormat(c.format)
    if (formula.toLowerCase() === OTHERWISE_FORMULA) {
      out.push(newRule({ kind: 'otherwise', ...fmt }))
      continue
    }
    const b = BETWEEN_RE.exec(formula)
    if (b) {
      out.push(newRule({ op: 'between', against: 'number', value: b[1], value2: b[2], ...fmt }))
      continue
    }
    const m = SIMPLE_RE.exec(formula)
    if (m && (m[3] === undefined || m[3].trim().toLowerCase() !== 'value')) {
      const raw = m[1] === '<>' ? '!=' : m[1] === '==' ? '=' : m[1]
      out.push(newRule({
        op: raw as RuleOp,
        against: m[3] !== undefined ? 'field' : 'number',
        value: m[2] ?? '',
        field: m[3] !== undefined ? m[3].trim() : '',
        ...fmt,
      }))
      continue
    }
    out.push(newRule({ kind: 'custom', formula, ...fmt }))
  }
  return out
}

/** Why a rule cannot be saved yet ('' = fine). */
export function ruleProblem(r: ColourRule): string {
  if (!r.theme && !/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(r.color.trim())) return 'Pick a colour theme or a colour.'
  if (r.kind === 'otherwise') return ''
  if (r.kind === 'custom') {
    if (!r.formula.trim()) return 'Write the condition.'
    if (r.formula.includes('~') || r.formula.includes('$')) return 'A condition cannot contain ~ or $.'
    return ''
  }
  if (r.against === 'field') return r.field ? '' : 'Choose the field to compare with.'
  const n = (s: string) => s.trim() !== '' && Number.isFinite(Number(s))
  if (!n(r.value)) return 'Type a number.'
  if (r.op === 'between') {
    if (!n(r.value2)) return 'Type the second number.'
    if (Number(r.value2) < Number(r.value)) return 'The second number must not be smaller than the first.'
  }
  return ''
}

function formulaOf(r: ColourRule): string {
  if (r.kind === 'otherwise') return OTHERWISE_FORMULA
  if (r.kind === 'custom') return r.formula.trim()
  if (r.op === 'between') return `#value# >= ${Number(r.value)} && #value# <= ${Number(r.value2)}`
  const rhs = r.against === 'field' ? `#${r.field}#` : String(Number(r.value))
  return `#value# ${r.op} ${rhs}`
}

/** The rule's format object — its colour key first, then what else it carried
 *  (so a saved rule reads back byte-for-byte). */
function formatOf(r: ColourRule): Record<string, string> {
  const { theme: _t, backgroundColor, ...rest } = r.extra
  if (r.theme) return { theme: r.theme, ...(backgroundColor ? { backgroundColor } : {}), ...rest }
  return { backgroundColor: r.color.trim(), ...rest }
}

/** The rules → ADV_FORMAT. Rules with a problem are left out (the screen
 *  shows why); the catch-all always goes last. */
export function serializeColourRules(rules: ColourRule[]): string {
  const ordered = [...rules.filter((r) => r.kind !== 'otherwise'), ...rules.filter((r) => r.kind === 'otherwise').slice(0, 1)]
  return ordered
    .filter((r) => !ruleProblem(r))
    .map((r) => `${formulaOf(r)}~${JSON.stringify(formatOf(r))}`)
    .join('$')
}

/** One plain sentence for a rule ("Sales is less than Target"). */
export function describeRule(r: ColourRule, measure: string, fieldLabel: (name: string) => string): string {
  if (r.kind === 'otherwise') return 'Everything else'
  if (r.kind === 'custom') return r.formula.replace(/#value#/gi, measure).replace(/#([^#]+)#/g, (_m, n: string) => fieldLabel(n.trim()))
  if (r.op === 'between') return `${measure} is between ${r.value || '…'} and ${r.value2 || '…'}`
  const rhs = r.against === 'field' ? (r.field ? fieldLabel(r.field) : '…') : (r.value || '…')
  return `${measure} ${opWords(r.op)} ${rhs}`
}
